/**
 * Location Map
 *
 * Draws a custom-styled Google Map into any `[data-location-map]` element. This
 * file is bundled into the site-wide blocks entry, so it runs on every page and
 * has to be inert where the block is absent — it is, because everything hangs
 * off that one query.
 *
 * ── Why the classic Marker, and no mapId ───────────────────────────────────
 * The whole point of this block is the designer's palette, and the JSON `styles`
 * array below is the only way to get it without a Google Cloud Console step per
 * environment. Passing a `mapId` would switch the map to cloud-based styling and
 * make `styles` silently do nothing — a failure that looks like "the style did
 * not apply" rather than like a mistake. No mapId therefore means no
 * AdvancedMarkerElement (it requires one), which is why this uses the classic
 * google.maps.Marker. Marker is deprecated-but-supported; if that ever changes,
 * the migration is: create a cloud map style, put its id here, and delete the
 * styles array.
 *
 * ── The bottom bar ─────────────────────────────────────────────────────────
 * The Google logo (bottom left) and the "Map data © / Terms / Report a map
 * error" line (bottom right) are NOT removable, and not because the API lacks
 * an option for it. The Maps Platform Terms of Service forbid removing,
 * obscuring or altering Google attribution, and enforcement is at the level of
 * the Cloud project — i.e. it can take the key down, and the key is shared with
 * anything else the project serves. CSS that hides or covers them counts as
 * obscuring, so a display:none rule is not a loophole, it is the violation.
 *
 * The one part of that bar that IS ours to remove is the "Keyboard shortcuts"
 * button — see keyboardShortcuts below.
 *
 * If a genuinely clean map is a hard design requirement, the answer is a
 * different provider rather than a workaround: MapLibre GL with OpenStreetMap
 * or Stadia tiles needs only a small attribution control, and being vector it
 * would match the palette more exactly than this does. That is a real swap of
 * this file, not a tweak.
 *
 * See README.md in this folder for the full note, the two-key setup, and the
 * rest of the block's design decisions.
 */

/**
 * Palette-matched map style.
 *
 * Colours are the theme palette (see theme.json): s-lb-200 land, s-lb-300
 * roads, with a step either side of those two for hierarchy.
 *
 * ── Labels: off by default, then switched back on ──────────────────────────
 * The comp showed no text anywhere on the map, and a map with no text is a blue
 * texture with a pin in it — a visitor cannot tell what town they are looking
 * at. So labels start off and four kinds come back: city, neighbourhood,
 * highway (shields included) and arterial road names. Those are what people
 * actually orient by.
 *
 * POI and transit stay off, and should: a scatter of restaurant pins around a
 * practice's location competes with the one pin that matters.
 */
const MAP_STYLE = [
	/* Land. This IS theme.json's s-lb-200. Sampling the comp returned #e4eef4
	   and #e6eff5 for the land, which are the same colour either side of image
	   noise, and s-lb-200 sits between them — so this is the palette token
	   rather than a near-miss of it. */
	{ elementType: 'geometry', stylers: [{ color: '#E4EDF5' }] },

	{ elementType: 'labels', stylers: [{ visibility: 'off' }] },
	{ featureType: 'poi', stylers: [{ visibility: 'off' }] },
	{ featureType: 'transit', stylers: [{ visibility: 'off' }] },

	/* Built-up land sits a step LIGHTER than open land, not darker.
	   This is what was making the whole map read heavy. At the previous #DFE9F3
	   every parcel and building footprint was a shade darker than its
	   surroundings — and around a practice on a commercial strip, built-up land
	   is most of the frame, so a single wrong value dragged down the average
	   tone of the entire map rather than showing up as one wrong shape. */
	{ featureType: 'landscape.man_made', elementType: 'geometry', stylers: [{ color: '#E6EFF5' }] },

	{ featureType: 'water', elementType: 'geometry', stylers: [{ color: '#DCE7F1' }] },

	/* Roads, in three weights, all sampled from the comp.
	   These are deliberately NOT s-lb-300 (#BACDE0), even though it looks like
	   the obvious token: it falls between the local and arterial tiers, so using
	   it flattens the two into each other and loses the road hierarchy the comp
	   clearly has. The stroke is off because Google's default road casing is a
	   darker outline that reads as a second, heavier line at these weights. */
	{ featureType: 'road', elementType: 'geometry', stylers: [{ color: '#C8D9E7' }] },
	{ featureType: 'road', elementType: 'geometry.stroke', stylers: [{ visibility: 'off' }] },
	{ featureType: 'road.arterial', elementType: 'geometry', stylers: [{ color: '#B1C8DB' }] },
	{ featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#A8C1D6' }] },

	/* ── The labels that come back ───────────────────────────────────────────
	   Google applies these rules in array order, so each one re-enables a slice
	   of the blanket `labels` off above. Order matters: move one of these ahead
	   of that rule and it is switched straight back off. */

	/* Boundary LINES stay off — only the place names come back. Without this
	   the county and city outlines return along with their labels and read as
	   stray roads. */
	{ featureType: 'administrative', elementType: 'geometry', stylers: [{ visibility: 'off' }] },

	/* Locality carries the town name and fades out around z15; neighbourhood
	   picks up from about z15 and runs higher. Both are on so there is a name
	   on screen across the zoom range the block's Zoom field allows. */
	{ featureType: 'administrative.locality', elementType: 'labels.text', stylers: [{ visibility: 'on' }] },
	{ featureType: 'administrative.neighborhood', elementType: 'labels.text', stylers: [{ visibility: 'on' }] },

	/* `labels`, not `labels.text` — the interstate shield is drawn as
	   labels.icon, and it is the single most useful mark on the map for a
	   practice off a highway exit. */
	{ featureType: 'road.highway', elementType: 'labels', stylers: [{ visibility: 'on' }] },

	/* Text only. Arterials have no shield worth showing, and their labels.icon
	   is where Google puts the exit-number markers. */
	{ featureType: 'road.arterial', elementType: 'labels.text', stylers: [{ visibility: 'on' }] },

	/* Local street names stay off — at z14–15 they are dense enough to read as
	   noise. Raise the Zoom field past 16 and they are still worth reconsidering
	   here rather than left to Google's defaults. */

	/* One palette for whatever survived, applied last so it reaches every label
	   regardless of feature. s-db-600 text on a white halo: the halo is what
	   keeps a road name legible where it crosses its own road fill. */
	{ elementType: 'labels.text.fill', stylers: [{ color: '#133D66' }] },
	{ elementType: 'labels.text.stroke', stylers: [{ color: '#FFFFFF' }, { weight: 4 }] },
];

/**
 * The pin, as an inline SVG data URI rather than a file — it is a handful of
 * bytes, it needs to be recolourable from this file, and it avoids a second
 * network request for something the map cannot draw without.
 *
 * The anchor is the point of the teardrop, not its centre, which is what makes
 * the pin sit ON the address rather than half a pin north of it.
 */
const PIN = {
	width: 32,
	height: 44,
	svg:
		'<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 44">' +
		'<path fill="#2A6AA7" d="M16 0C7.163 0 0 7.163 0 16c0 10.5 14.06 26.62 14.66 27.3a1.8 1.8 0 0 0 2.68 0C17.94 42.62 32 26.5 32 16 32 7.163 24.837 0 16 0Z"/>' +
		'<circle cx="16" cy="15.5" r="5.5" fill="#ffffff"/>' +
		'</svg>',
};

/**
 * One loader for the whole page, however many maps are on it. The Maps API can
 * only be loaded once — a second script tag logs "You have included the Google
 * Maps JavaScript API multiple times" and can leave the API in a broken state —
 * so the promise is cached and every map awaits the same one.
 */
let mapsApi = null;

function loadMapsApi(key) {
	if (mapsApi) return mapsApi;

	mapsApi = new Promise((resolve, reject) => {
		// Google calls a named global, so the callback has to live on window.
		const callbackName = '__ogLocationMapReady';

		window[callbackName] = () => {
			delete window[callbackName];
			resolve(window.google.maps);
		};

		const script = document.createElement('script');
		// `loading=async` is what tells Google the script is loaded
		// asynchronously; without it the console warns about it on every load.
		script.src =
			'https://maps.googleapis.com/maps/api/js?key=' +
			encodeURIComponent(key) +
			'&loading=async&callback=' +
			callbackName;
		script.async = true;
		script.onerror = () => reject(new Error('Google Maps failed to load.'));
		document.head.appendChild(script);
	});

	return mapsApi;
}

function drawMap(el, maps) {
	const lat = parseFloat(el.dataset.mapLat);
	const lng = parseFloat(el.dataset.mapLng);
	if (Number.isNaN(lat) || Number.isNaN(lng)) return;

	const center = { lat, lng };

	const map = new maps.Map(el, {
		center,
		zoom: parseInt(el.dataset.mapZoom, 10) || 14,
		styles: MAP_STYLE,

		// The comp has no controls, and a map this small has no room for them.
		disableDefaultUI: true,

		// Removes the "Keyboard shortcuts" button from the bottom bar. Note that
		// it removes the shortcuts too, not just the button — arrow-key panning
		// and +/- zooming go with it. That is an accessibility cost, and it is
		// only acceptable because the map is not where the information lives:
		// the address and the directions link are both reachable without it,
		// and the directions link is the actual task.
		//
		// ⚠ This is the ONLY item in that bottom bar that can be removed. The
		// Google logo and the "Map data © / Terms / Report a map error" line are
		// required by the Maps Platform Terms of Service and there is no option
		// for them — see the note at the top of this file.
		keyboardShortcuts: false,

		// Google's own POI pins stay clickable even with POI labels styled off,
		// so a stray click can open an info window for a neighbouring business.
		clickableIcons: false,

		// Plain scrolling pans the PAGE over the map; zooming needs ctrl/⌘ (or
		// two fingers on a touch screen). Without this a phone user who starts a
		// swipe on the map is stuck panning the map instead of scrolling the
		// page — a trap, and a common one.
		gestureHandling: 'cooperative',
	});

	const marker = new maps.Marker({
		position: center,
		map,
		title: el.dataset.mapTitle || '',
		cursor: 'pointer',
		icon: {
			url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(PIN.svg),
			scaledSize: new maps.Size(PIN.width, PIN.height),
			anchor: new maps.Point(PIN.width / 2, PIN.height),
		},
	});

	// No info window. The location card in index.twig does that job in real
	// HTML — see the note there for why an InfoWindow was the wrong tool — so
	// the pin only has to handle the click people are already trying to make.
	// The card carries the keyboard-reachable directions link.
	const directions = el.dataset.mapDirections;
	if (directions) {
		marker.addListener('click', () => {
			window.open(directions, '_blank', 'noopener,noreferrer');
		});
	}
}

/**
 * Deferred until the map is close to the viewport. The Maps API is a large
 * script plus tiles; loading it for a map three screens down is bandwidth spent
 * on something that may never be seen, and it competes with rendering the part
 * of the page that is.
 */
function observe(el) {
	// Guard against a second pass over the same element. The editor re-renders a
	// block on every field change, and the MutationObserver below catches those,
	// but the initial scan and the observer can both reach the same node.
	if (el.dataset.mapReady) return;
	el.dataset.mapReady = 'true';

	const key = el.dataset.mapKey;
	if (!key) return;

	const start = () => {
		loadMapsApi(key)
			.then((maps) => drawMap(el, maps))
			.catch((error) => console.error('[Location Map]', error));
	};

	if (!('IntersectionObserver' in window)) {
		start();
		return;
	}

	const observer = new IntersectionObserver(
		(entries) => {
			entries.forEach((entry) => {
				if (!entry.isIntersecting) return;
				observer.disconnect();
				start();
			});
		},
		// Start loading before it scrolls into view, so the map is drawn by the
		// time it arrives rather than appearing as a grey box that fills in.
		{ rootMargin: '300px' }
	);

	observer.observe(el);
}

function scan(root = document) {
	root.querySelectorAll('[data-location-map]').forEach(observe);
}

function init() {
	scan();

	// The block editor renders blocks after this script has run, and re-renders
	// them on every field change, so a one-time scan would only ever find maps
	// on the front end. Watching for added nodes covers the editor and anything
	// else that injects markup later.
	if (!('MutationObserver' in window)) return;

	new MutationObserver((mutations) => {
		mutations.forEach((mutation) => {
			mutation.addedNodes.forEach((node) => {
				if (node.nodeType !== Node.ELEMENT_NODE) return;
				if (node.matches('[data-location-map]')) observe(node);
				scan(node);
			});
		});
	}).observe(document.body, { childList: true, subtree: true });
}

if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', init);
} else {
	init();
}
